import React, { useEffect, useMemo, useState } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHead,
  BlockBetween,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
} from "../../components/Component";
import { defaultParams } from "../../constants";
import BasePaginationTable from "../../components/table/BasePaginationTable";
import {
  addMiLibraryCategory,
  deleteMiLibraryCategory,
  getMiLibraryCategories,
  updateMiLibraryCategory,
} from "../../api/mi-library";
import DeleteConfirmationModal from "../admin/modals/DeleteConfimationModal";
import {
  Modal,
  ModalBody,
  Form,
  Spinner,
  Label as RsLabel,
} from "reactstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

// MiLibraryCategory.name is now { en, ar? }; legacy rows may still be a
// plain string until the BE migration runs.
const readNameEn = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name.en ?? "";
};
const readNameAr = (name) => {
  if (!name || typeof name === "string") return "";
  return name.ar ?? "";
};

const getDefaultColumns = (onEdit, onDelete) => [
  {
    head: <span className="sub-text">{"Name"}</span>,
    cell: (row) => {
      const en = readNameEn(row?.name);
      const ar = readNameAr(row?.name);
      return (
        <div className="d-flex flex-column">
          <span>{en || "-"}</span>
          {ar && (
            <span className="text-muted" style={{ fontSize: 12 }}>
              {ar}
            </span>
          )}
        </div>
      );
    },
  },
  {
    head: <span className="sub-text">{"Assets"}</span>,
    cell: (row) => <span>{row?.assetsCount ?? 0}</span>,
  },
  {
    head: <span className="sub-text"></span>,
    cellClassName: "nk-tb-col-tools",
    cell: (row) => (
      <ul
        className="nk-tb-actions gx-1"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <li>
          <a
            href="#edit"
            className="btn btn-icon btn-trigger"
            onClick={(ev) => {
              ev.preventDefault();
              onEdit(row);
            }}
          >
            <Icon name="edit" />
          </a>
        </li>
        <li>
          <a
            href="#delete"
            className="btn btn-icon btn-trigger btn-tooltip link-danger"
            onClick={(ev) => {
              ev.preventDefault();
              onDelete(row);
            }}
          >
            <Icon name="trash" />
          </a>
        </li>
      </ul>
    ),
  },
];

const CategoryModal = ({ isOpen, onClose, editing, onSaved }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({ defaultValues: { nameEn: "", nameAr: "" } });

  useEffect(() => {
    reset({
      nameEn: readNameEn(editing?.name),
      nameAr: readNameAr(editing?.name),
    });
  }, [editing, reset]);

  const onSubmit = async (data) => {
    const ar = (data.nameAr ?? "").trim();
    const payload = {
      name: ar
        ? { en: data.nameEn.trim(), ar }
        : { en: data.nameEn.trim() },
    };
    try {
      if (editing?._id) {
        const res = await updateMiLibraryCategory(editing._id, payload);
        onSaved(res, true);
        toast.success("Update category successfully");
      } else {
        const res = await addMiLibraryCategory(payload);
        onSaved(res, false);
        toast.success("Add category successfully");
      }
      onClose();
    } catch (error) {
      toast.error(error?.message || "Save category failed");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="modal-dialog-centered">
      <ModalBody>
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            onClose();
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <div className="p-2">
          <h5 className="title">
            {editing?._id ? "Edit Category" : "Add Category"}
          </h5>
          <div className="mt-4">
            <Form
              className="row gy-3"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="form-group">
                <RsLabel className="form-label">Name (English)</RsLabel>
                <div className="form-control-wrap">
                  <input
                    type="text"
                    className="form-control"
                    {...register("nameEn", {
                      required: "Name (English) is required",
                    })}
                    placeholder="Enter category name in English"
                  />
                  {errors.nameEn && (
                    <span className="invalid">{errors.nameEn.message}</span>
                  )}
                </div>
              </div>
              <div className="form-group">
                <RsLabel className="form-label">Name (Arabic)</RsLabel>
                <div className="form-control-wrap">
                  <input
                    type="text"
                    className="form-control"
                    dir="rtl"
                    {...register("nameAr")}
                    placeholder="أدخل اسم الفئة بالعربية"
                  />
                  {errors.nameAr && (
                    <span className="invalid">{errors.nameAr.message}</span>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Button color="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Spinner size="sm" color="light" />
                  ) : (
                    "Save"
                  )}
                </Button>
                <a
                  href="#cancel"
                  onClick={(ev) => {
                    ev.preventDefault();
                    onClose();
                  }}
                  className="link link-light"
                >
                  Cancel
                </a>
              </div>
            </Form>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

const MiLibraryCategoriesPage = () => {
  const [params, setParams] = useState(defaultParams);
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState({ open: false, editing: null });
  const [deleting, setDeleting] = useState(null);

  const fetchCategories = async (query = params) => {
    setLoading(true);
    try {
      const res = await getMiLibraryCategories(query);
      const docs = res?.docs || res?.items || res?.categories || res?.data || res || [];
      const summaryData = res?.summary || res?.pagination;
      setList(docs);
      setSummary(summaryData);
    } catch (error) {
      toast.error(error?.message || "Load categories failed");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCategories(params);
  }, [params]);

  const handlePageChange = (page) => {
    setParams((prev) => ({ ...prev, page }));
  };

  const columns = useMemo(
    () => getDefaultColumns((cat) => setModalState({ open: true, editing: cat }), setDeleting),
    []
  );

  const handleSaveCategory = (cat, isUpdate) => {
    if (isUpdate) {
      setList((prev) => prev.map((c) => (c._id === cat._id ? cat : c)));
    } else {
      setList((prev) => [cat, ...prev]);
      setSummary((prev) =>
        prev ? { ...prev, count: (prev.count || 0) + 1 } : prev
      );
    }
  };

  const handleDelete = async () => {
    if (!deleting?._id) return;
    try {
      await deleteMiLibraryCategory(deleting._id);
      setList((prev) => prev.filter((c) => c._id !== deleting._id));
      setSummary((prev) =>
        prev ? { ...prev, count: Math.max((prev.count || 1) - 1, 0) } : prev
      );
      toast.success("Delete category successfully");
    } catch (error) {
      toast.error(error?.message || "Delete category failed");
    }
    setDeleting(null);
  };

  return (
    <React.Fragment>
      <Head title="MI Library Categories"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>MI Library Categories</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                onClick={() => setModalState({ open: true, editing: null })}
              >
                <Icon name="plus"></Icon>
                <span className="ms-1">Add Category</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <BasePaginationTable
          list={list}
          loading={loading}
          columns={columns}
          onPageChange={handlePageChange}
          hasNextPage={summary?.hasNextPage}
          totalItems={summary?.count}
          {...params}
        />
      </Content>

      <CategoryModal
        isOpen={modalState.open}
        editing={modalState.editing}
        onSaved={handleSaveCategory}
        onClose={() => setModalState({ open: false, editing: null })}
      />

      <DeleteConfirmationModal
        onConfirm={handleDelete}
        isOpen={Boolean(deleting)}
        onCloseModal={() => setDeleting(null)}
      />
    </React.Fragment>
  );
};

export default MiLibraryCategoriesPage;
